import React, { useState } from 'react';
import { mdiAlert, mdiAlertOctagon, mdiInformation, mdiCheckCircleOutline, mdiCrosshairsGps, mdiCheck, mdiBellOff } from '@mdi/js';
import { Diagnostico } from '@tera/shared/src/types/Diagnostico';
import Botao from '../Botao';
import Modal from './Modal';
import Badge from './Badge';
import VBox from './VBox';
import HBox from './HBox';
import Icon from '../Icon';
import axios from 'axios';

type Props = {
  diagnosticos: Diagnostico[];
  onSelectNosAfetados?: (nodeIds: string[]) => void;
  dialogoId?: number;
  onAck?: () => void;
};

const IconeDiagnostico = ({ diagnosticos, onSelectNosAfetados, dialogoId, onAck }: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [ackingKey, setAckingKey] = useState<string | null>(null);

  if (!diagnosticos || diagnosticos.length === 0) return <div>
    <Icon
      path={mdiCheckCircleOutline}
      size={1.5}
      color="var(--tc-color-gray-400)"
      style={{ display: 'block', margin: 'auto auto', width: '2rem', height: '1.5rem' }}
      title="Diagnóstico: Tudo certo!"
    />
  </div>;

  const diagnosticosNaoAck = diagnosticos.filter((d) => !d.ackAt);
  const todosAck = diagnosticosNaoAck.length === 0;

  // Prioridade de exibição no ícone principal: impedimento > aviso > info
  const temImpedimento = diagnosticosNaoAck.some((d) => d.tipo === 'impedimento');
  const temAviso = diagnosticosNaoAck.some((d) => d.tipo === 'aviso');
  const icon = temImpedimento ? mdiAlertOctagon : temAviso ? mdiAlert : mdiInformation;
  const variant = todosAck ? 'none-secondary' : temImpedimento ? 'none-danger' : temAviso ? 'none-warning' : 'none-info';
  const iconProps = todosAck ? { color: 'var(--tc-color-gray-400)' } : undefined;

  const resumo =
    diagnosticos[0].mensagem +
    (diagnosticos.length > 1 ? ` (+${diagnosticos.length - 1})` : '');

  const handleAck = async (d: Diagnostico) => {
    if (!dialogoId) return;
    const key = `${d.codigo}|${[...d.nosAfetados].sort().join(',')}`;
    setAckingKey(key);
    try {
      await axios.post(`/api/dialogo/${dialogoId}/diagnosticos/ack`, {
        codigo: d.codigo,
        nosAfetados: d.nosAfetados,
      });
      onAck?.();
    } finally {
      setAckingKey(null);
    }
  };

  return (
    <>
      <Botao
        variant={variant}
        icon={icon}
        iconProps={iconProps}
        onClick={(e: any) => {
          e.preventDefault();
          e.stopPropagation();
          setShowModal(true);
        }}
        title={resumo}
      />

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="Diagnóstico do fluxo"
        hideControls
      >
        <VBox gap="12px">
          {diagnosticos.map((d, i) => {
            const key = `${d.codigo}|${[...d.nosAfetados].sort().join(',')}`;
            const isAcking = ackingKey === key;
            return (
              <HBox key={i} gap="12px" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Badge variant={d.ackAt ? 'secondary' : d.tipo === 'impedimento' ? 'danger' : d.tipo === 'aviso' ? 'warning' : 'info'}>
                  {d.tipo === 'impedimento' ? 'Impedimento' : d.tipo === 'aviso' ? 'Aviso' : 'Dica'}
                </Badge>
                <span style={{ flex: 1, color: d.ackAt ? 'var(--tc-color-gray-500)' : undefined }}>
                  {d.mensagem}
                </span>
                {onSelectNosAfetados && d.nosAfetados?.length > 0 && (
                  <Botao
                    variant="outline-secondary"
                    icon={mdiCrosshairsGps}
                    title="Localizar no editor"
                    onClick={() => {
                      setShowModal(false);
                      onSelectNosAfetados(d.nosAfetados);
                    }}
                  />
                )}
                {dialogoId && !d.ackAt && (
                  <Botao
                    variant="outline-secondary"
                    icon={mdiBellOff}
                    title="Reconhecer alerta"
                    disabled={isAcking}
                    onClick={() => handleAck(d)}
                  />
                )}
                {d.ackAt && (
                  <Botao variant="outline-secondary" disabled icon={mdiCheck} title="Alerta reconhecido" />
                )}
              </HBox>
            );
          })}
        </VBox>
      </Modal>
    </>
  );
};

export default IconeDiagnostico;
